import {
  minZoomWidthMs,
  mouseWheelZoomSpeed,
  pinchZoomSpeed,
  wheelLineHeightPx,
  wheelPageHeightPx,
} from './chartZoom.constants.ts';

export type ZoomRange = { max: number; min: number };

export type WheelGesture = { type: 'pan'; deltaPx: number } | { type: 'zoom'; factor: number };

type WheelInput = { ctrlKey: boolean; deltaMode?: number; deltaX: number; deltaY: number };

const deltaModeToPx = (deltaMode: number) => {
  if (deltaMode === 1) return wheelLineHeightPx;
  if (deltaMode === 2) return wheelPageHeightPx;
  return 1;
};

/**
 * Wheel gestures: plain wheel zooms, ctrl+wheel pans, a trackpad two-finger swipe (horizontal
 * delta) pans and a trackpad pinch zooms. The browser reports a pinch as a ctrl+wheel with a
 * fractional deltaY; a mouse wheel reports whole numbers.
 */
export const getWheelGesture = ({
  ctrlKey,
  deltaMode = 0,
  deltaX: rawDeltaX,
  deltaY: rawDeltaY,
}: WheelInput): WheelGesture | null => {
  const scale = deltaModeToPx(deltaMode);
  const deltaX = rawDeltaX * scale;
  const deltaY = rawDeltaY * scale;

  if (ctrlKey && !Number.isInteger(rawDeltaY)) {
    return { type: 'zoom', factor: Math.exp(deltaY * pinchZoomSpeed) };
  }
  if (ctrlKey) return deltaY === 0 ? null : { type: 'pan', deltaPx: deltaY };
  if (deltaX !== 0) return { type: 'pan', deltaPx: deltaX };
  if (deltaY !== 0) return { type: 'zoom', factor: Math.exp(deltaY * mouseWheelZoomSpeed) };
  return null;
};

/** Keeps `range` inside `base` without changing its width (capped at the base width). */
export const clampRange = ({ base, range }: { base: ZoomRange; range: ZoomRange }): ZoomRange => {
  const width = Math.min(range.max - range.min, base.max - base.min);
  const min = Math.min(Math.max(range.min, base.min), base.max - width);
  return { min, max: min + width };
};

/**
 * Scales the visible width by `factor` (below 1 zooms in) around `anchorRatio` (0 = left edge,
 * 1 = right edge of the plot). Never zooms out past `base`.
 */
export const zoomRange = ({
  anchorRatio,
  base,
  factor,
  range,
}: {
  anchorRatio: number;
  base: ZoomRange;
  factor: number;
  range: ZoomRange;
}): ZoomRange => {
  const baseWidth = base.max - base.min;
  const rangeWidth = range.max - range.min;
  const width = Math.min(
    Math.max(rangeWidth * factor, Math.min(minZoomWidthMs, baseWidth)),
    baseWidth
  );
  const anchor = range.min + anchorRatio * rangeWidth;
  const min = anchor - anchorRatio * width;

  return clampRange({ base, range: { min, max: min + width } });
};

export const panRange = ({
  base,
  delta,
  range,
}: {
  base: ZoomRange;
  delta: number;
  range: ZoomRange;
}): ZoomRange => clampRange({ base, range: { min: range.min + delta, max: range.max + delta } });

export const isZoomedIn = ({ base, range }: { base: ZoomRange; range: ZoomRange }) =>
  range.max - range.min < base.max - base.min - 1;
